import React from "react";
import {
  Autocomplete,
  Box,
  Divider,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import TitleLabel from "../utils/FormTitle";
import {
  TextFieldForName,
  styleForTextField,
} from "../../../utils/TextField/TextField";
import { DropDownByObj } from "../../../utils/Form/Dropdown";
import { declarantDesignationOptions } from "../utils/options";

const UEN_TYPE_OPTIONS = [
  { value: "0", label: "Local Companies with UEN (yyyynnnnnX format)" },
  { value: "1", label: "Foreign Companies with UEN (TyyPQnnnnX format)" },
  { value: "2", label: "Others without UEN" },
];

const DESIGNATION_SUGGESTIONS = [
  ...new Set(declarantDesignationOptions.flatMap((opt) => [opt.value])),
];

const FormCSDesignationField = ({ value, onChange, disabled, placeholder }) => (
  <Autocomplete
    freeSolo
    fullWidth
    disabled={disabled}
    options={DESIGNATION_SUGGESTIONS}
    value={value || ""}
    onChange={(_, newValue) => onChange(newValue ?? "")}
    onInputChange={(_, newInputValue, reason) => {
      if (reason === "input" || reason === "clear") {
        onChange(newInputValue);
      }
    }}
    renderInput={(params) => (
      <TextField
        {...params}
        size='small'
        fullWidth
        placeholder={placeholder}
        sx={{
          ...styleForTextField(),
          "& .MuiOutlinedInput-root": {
            ...(styleForTextField()["& .MuiOutlinedInput-root"] || {}),
            borderRadius: 1,
          },
        }}
      />
    )}
  />
);

const SectionHeading = ({ children }) => (
  <Typography variant='subtitle1' fontWeight={700} sx={{ mt: 0.5 }}>
    {children}
  </Typography>
);

const FormCSFilingInfoSection = ({ filingInfo, onChange, disabled }) => {
  const set = (field, value) => {
    onChange({ ...filingInfo, [field]: value });
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <BusinessIcon color='primary' />
          <Typography variant='h6' fontWeight={600}>
            Filing information
          </Typography>
        </Box>
        <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
          Company and contact details for the Form C-S submission.
        </Typography>
      </Grid>

      <Grid item xs={12} sm={6}>
        <TitleLabel title='UEN' required />
        <TextFieldForName
          fullWidth
          value={filingInfo.uen}
          onChange={(val) => set("uen", val)}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TitleLabel title='UEN Type' required />
        <DropDownByObj
          size='small'
          maxWidth='100%'
          value={String(filingInfo.uenType ?? 0)}
          options={UEN_TYPE_OPTIONS}
          onChange={(e) => set("uenType", Number(e.target.value) || 0)}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TitleLabel title='Year of Assessment (YA)' required />
        <TextFieldForName
          fullWidth
          type='number'
          value={filingInfo.ya}
          onChange={(val) => set("ya", val === "" ? "" : Number(val))}
          disabled={true}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TitleLabel
          title='Declarant reference no.'
          info='Tax reference number assigned by IRAS to foreign individual not eligible for Singpass.'
        />
        <TextFieldForName
          fullWidth
          value={filingInfo.declarantRefNo}
          onChange={(val) => set("declarantRefNo", val)}
          disabled={disabled}
          placeholder='e.g. AxxxxxxxZ (foreign individual without Singpass)'
        />
      </Grid>

      <Grid item xs={12}>
        <Divider />
      </Grid>

      <Grid item xs={12}>
        <SectionHeading>Declarant &amp; Contact Details</SectionHeading>
      </Grid>

      <Grid item xs={12} sm={6}>
        <TitleLabel title='Designation of filer' required />
        <FormCSDesignationField
          value={filingInfo.designationOfFiler}
          onChange={(val) => set("designationOfFiler", val)}
          disabled={disabled}
          placeholder='Select or enter designation'
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TitleLabel title='Name of contact person' required />
        <TextFieldForName
          fullWidth
          value={filingInfo.nameOfContactPerson}
          onChange={(val) => set("nameOfContactPerson", val)}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TitleLabel title='Contact number' required />
        <TextFieldForName
          fullWidth
          value={filingInfo.contactNumber}
          onChange={(val) => set("contactNumber", val)}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TitleLabel title='Designation of contact person' required />
        <FormCSDesignationField
          value={filingInfo.designationOfContactPerson}
          onChange={(val) => set("designationOfContactPerson", val)}
          disabled={disabled}
          placeholder='Select or enter designation'
        />
      </Grid>
    </Grid>
  );
};

export default FormCSFilingInfoSection;
